import React, { FC, useMemo } from 'react';

import dayjs from 'utils/extendedDayJS';
import { GuestStatus } from 'types/guest.types';
import { ModalTitle, ModalFooter } from '@epoch-ui/components/Modal/modal';
import { Button } from '@epoch-ui/components/Button';
import { StepBar } from '@epoch-components/step-bar/StepBar';
import { SessionCard, SessionCardTitleAndDate } from 'components/cards/SessionCard';
import { RsvpSticker } from 'components/cards/components/RsvpSticker/RsvpSticker';
import ForwardArrowIcon from 'components/common/icons/ForwardArrowIcon';

import { getDisplayTextForStatus } from '../../../utils';
import { useRSVPControlModalContext } from '../rsvpControlContext';

export const SwapSessionModalContent: FC = () => {
  const {
    navigation,
    guest: { firstName },
    event: { timezoneCode },
    selectedStatus: newSessionStatus,
    currentSession: newSession,
    conflictingSessionAndStatus
  } = useRSVPControlModalContext();
  const oldSession = conflictingSessionAndStatus?.session;
  const oldSessionStatus = conflictingSessionAndStatus?.status;

  const eventTimezone = timezoneCode || 'UTC';
  const displayedTimezone = dayjs.tz.guess() || eventTimezone;
  const oldStatusDisplayText = useMemo(() => {
    if (!oldSessionStatus) return 'registered';
    return oldSessionStatus === GuestStatus.WAITLISTED
      ? getDisplayTextForStatus(oldSessionStatus).toLowerCase()
      : `already marked as ${getDisplayTextForStatus(oldSessionStatus).toLowerCase()}`;
  }, [oldSessionStatus]);
  const newStatusDisplayText = useMemo(
    () => getDisplayTextForStatus(newSessionStatus!),
    [newSessionStatus]
  );

  return (
    <>
      <ModalTitle className="tw-mb-0 tw-text-2xl tw-font-medium">Confirm session change</ModalTitle>
      {oldSession && (
        <div className="tw-flex tw-flex-col tw-gap-3">
          <span>
            {`${firstName} is ${oldStatusDisplayText} for a different session. \
                The proposed change to their RSVP will remove them from the following session:`}
          </span>
          <SessionCard
            className="tw-cursor-default tw-rounded-[12px]"
            session={oldSession}
            eventTimezone={eventTimezone}
            displayedTimezone={displayedTimezone}
          >
            <SessionCardTitleAndDate />
            <div className="tw-flex tw-items-center tw-justify-between">
              <RsvpSticker
                className="tw-rounded-xl tw-p-2"
                rsvpStatus={GuestStatus.CANCELLED}
                customText="Removed"
              />
            </div>
          </SessionCard>
        </div>
      )}
      {newSession && (
        <div className="tw-flex tw-flex-col tw-gap-3">
          <span>{`${firstName} will be added as ${newStatusDisplayText} to the following session instead:`}</span>
          <SessionCard
            className="tw-cursor-default tw-rounded-[12px]"
            session={newSession}
            eventTimezone={eventTimezone}
            displayedTimezone={displayedTimezone}
          >
            <SessionCardTitleAndDate />
            {newSessionStatus && (
              <div className="tw-flex tw-items-center tw-justify-between">
                <RsvpSticker className="tw-rounded-xl tw-p-2" rsvpStatus={newSessionStatus} />
              </div>
            )}
          </SessionCard>
        </div>
      )}
      <ModalFooter className="tw-mt-0 tw-flex tw-flex-row tw-justify-end">
        <div className="tw-flex tw-flex-1 tw-items-center">
          <StepBar
            totalSteps={navigation.totalStepNumber}
            currentStep={navigation.currentStepNumber}
          />
        </div>
        <div className="tw-flex tw-flex-row tw-justify-center tw-gap-4">
          <Button
            size="sm"
            onClick={navigation.onCancel}
            className="tw-flex-1 tw-rounded-[8px]"
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="tw-flex-1 tw-rounded-[8px]"
            variant="primary"
            onClick={navigation.onClickSubmit}
          >
            Change Session
            {navigation.currentStepNumber < navigation.totalStepNumber && (
              <div className="tw-ml-2">
                <ForwardArrowIcon color="white" />
              </div>
            )}
          </Button>
        </div>
      </ModalFooter>
    </>
  );
};
